"use client";

import "../careers.css";
import React from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { motion, AnimatePresence, MotionConfig } from "motion/react";
import CareersHeader from "../CareersHeader";
import type { Job } from "../lib";
import { CAREERS_CONVEX_URL } from "../lib";
import { getConfigForPosition, COUNTRIES } from "../formConfig";

// ─── Pure helpers ────────────────────────────────────────────

const stripPrefix = (value: string, prefixes: string[]) => {
	const v = value.trim();
	for (const p of prefixes) {
		if (v.toLowerCase().startsWith(p.toLowerCase())) return v.slice(p.length);
	}
	return v;
};

const formatUrl = (url: string): string => {
	const trimmed = url.trim();
	if (!trimmed) return trimmed;
	if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
	return `https://${trimmed}`;
};

// ─── Validation ──────────────────────────────────────────────

function validateEmail(email: string): boolean {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function validateRequired(fields: { label: string; value: string }[]): string | null {
	for (const f of fields) {
		if (!f.value.trim()) return `${f.label} is required.`;
	}
	return null;
}

// ─── Application Form ────────────────────────────────────────

type FormState = "idle" | "submitting" | "success";

function ApplicationForm({ job }: { job: Job }) {
	const config = getConfigForPosition(job.title);

	const [firstName, setFirstName] = React.useState("");
	const [lastName, setLastName] = React.useState("");
	const [email, setEmail] = React.useState("");
	const [country, setCountry] = React.useState("");
	const [linkedIn, setLinkedIn] = React.useState("");
	const [twitter, setTwitter] = React.useState("");
	const [portfolio, setPortfolio] = React.useState("");
	const [resume, setResume] = React.useState<File | null>(null);
	const [acknowledged, setAcknowledged] = React.useState(false);
	const [customAnswers, setCustomAnswers] = React.useState<Record<string, string>>(() => {
		const initial: Record<string, string> = {};
		for (const q of config.customQuestions) initial[q.id] = "";
		return initial;
	});
	const [state, setState] = React.useState<FormState>("idle");
	const [error, setError] = React.useState("");

	const validate = (): string | null => {
		const required: { label: string; value: string }[] = [
			{ label: "First name", value: firstName },
			{ label: "Last name", value: lastName },
			{ label: "Email", value: email },
			{ label: "Country", value: country },
		];

		if (config.requiresPortfolio) required.push({ label: config.portfolioLabel || "Portfolio", value: portfolio });
		if (config.requiresLinkedIn) required.push({ label: "LinkedIn", value: linkedIn });

		for (const q of config.customQuestions) {
			if (q.required) required.push({ label: q.question, value: customAnswers[q.id] || "" });
		}

		const missing = validateRequired(required);
		if (missing) return missing;
		if (!validateEmail(email)) return "Please enter a valid email address.";
		if (resume) {
			const maxSize = 10 * 1024 * 1024; // 10MB
			if (resume.size > maxSize) return "Resume must be under 10MB.";
			const allowedTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
			if (!allowedTypes.includes(resume.type)) return "Resume must be a PDF, DOC, or DOCX file.";
		}
		if (!acknowledged) return "Please acknowledge the privacy notice to continue.";

		return null;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");

		const validationError = validate();
		if (validationError) {
			setError(validationError);
			setTimeout(() => document.querySelector('[role="alert"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
			return;
		}

		setState("submitting");

		try {
			let resumeUrl: string | undefined;

			// Upload resume if provided
			if (resume) {
				const uploadUrlRes = await fetch(`${CAREERS_CONVEX_URL}/api/mutation`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ path: "storage:generateUploadUrl", args: {}, format: "json" }),
				});
				if (!uploadUrlRes.ok) throw new Error("Failed to get upload URL");
				const { value: uploadUrl } = await uploadUrlRes.json();

				const uploadRes = await fetch(uploadUrl, {
					method: "POST",
					headers: { "Content-Type": resume.type },
					body: resume,
				});
				if (!uploadRes.ok) throw new Error("Failed to upload resume");
				const { storageId } = await uploadRes.json();
				resumeUrl = storageId;
			}

			const answers = config.customQuestions.map((q) => ({
				question: q.question,
				answer: customAnswers[q.id] || "",
			}));

			const res = await fetch(`${CAREERS_CONVEX_URL}/api/mutation`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					path: "applications:create",
					args: {
						jobId: job._id,
						jobTitle: job.title,
						fullName: `${firstName.trim()} ${lastName.trim()}`,
						email: email.trim().toLowerCase(),
						country: country.trim(),
						portfolio: portfolio ? formatUrl(portfolio) : undefined,
						twitter: twitter ? formatUrl(twitter) : undefined,
						linkedIn: linkedIn.trim() || undefined,
						resumeUrl,
						customAnswers: answers,
					},
					format: "json",
				}),
			});

			if (!res.ok) throw new Error("Failed to submit");
			setState("success");
		} catch {
			setError("Something went wrong. Please try again.");
			setState("idle");
		}
	};

	const requiredStar = <span className="text-black/32"> *</span>;

	return (
		<MotionConfig reducedMotion="user">
		<AnimatePresence mode="wait">
		{state === "success" ? (
			<motion.div
				key="success"
				initial={{ opacity: 0, y: 8 }}
				animate={{ opacity: 1, y: 0 }}
				exit={{ opacity: 0, y: 8 }}
				transition={{ duration: 0.1, ease: "easeOut" }}
				className="flex flex-col items-center gap-4 py-16 text-center"
			>
				<svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
					<circle cx="16" cy="16" r="16" fill="rgba(0,0,0,0.88)" />
					<path d="M10 16.5L14 20.5L22 12.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
				<p className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111]">Application submitted</p>
				<p className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">
					We received your application for {job.title}. We will review it and get back to you soon.
				</p>
				<Link href="/careers" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] mt-2 text-[#2563eb]">
					View all positions
				</Link>
			</motion.div>
		) : (
		<motion.form
			key="form"
			initial={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: 8, pointerEvents: "none" as const }}
			transition={{ duration: 0.1, ease: "easeIn" }}
			onSubmit={handleSubmit} noValidate className="flex flex-col gap-6 w-full">
			<h2 className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111]">Apply</h2>

			{/* 1. Personal Information */}
			<div className="flex flex-col gap-6">
				<div className="form-grid">
					<div className="flex flex-col gap-2">
						<label htmlFor="field-firstname" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">First name{requiredStar}</label>
						<input id="field-firstname" name="firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" aria-required="true" className="w-full border border-black/[0.12] rounded-lg bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111] font-[inherit]" />
					</div>
					<div className="flex flex-col gap-2">
						<label htmlFor="field-lastname" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">Last name{requiredStar}</label>
						<input id="field-lastname" name="lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" aria-required="true" className="w-full border border-black/[0.12] rounded-lg bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111] font-[inherit]" />
					</div>
				</div>
				<div className="form-grid">
					<div className="flex flex-col gap-2">
						<label htmlFor="field-email" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">Email{requiredStar}</label>
						<input id="field-email" name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" spellCheck={false} aria-required="true" className="w-full border border-black/[0.12] rounded-lg bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111] font-[inherit]" />
					</div>
					<div className="flex flex-col gap-2">
						<label htmlFor="field-country" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">Country{requiredStar}</label>
					<div className="relative">
						<select id="field-country" name="country" value={country} onChange={(e) => setCountry(e.target.value)} autoComplete="country-name" aria-required="true" className={`w-full border border-black/[0.12] rounded-lg bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] font-[inherit] cursor-pointer appearance-none pr-9 box-border ${country ? "text-[#111]" : "text-black/[0.24]"}`}>
							<option value="">Select country{"\u2026"}</option>
							{COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
						</select>
						<svg width="12" height="12" viewBox="0 0 28 28" fill="none" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true">
							<path d="M23.3332 10.5L13.9999 19.8333L4.6665 10.5" stroke="rgba(0,0,0,0.24)" strokeWidth="2.5" strokeLinecap="square" />
						</svg>
					</div>
				</div>
				</div>
			</div>

				{/* Portfolio / Website */}
				{(config.requiresPortfolio || config.portfolioLabel) && (
					<div className="flex flex-col gap-2">
						<label htmlFor="field-portfolio" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">{config.portfolioLabel || "Portfolio"}{config.requiresPortfolio && requiredStar}</label>
						<input id="field-portfolio" name="portfolio" type="url" value={portfolio} onChange={(e) => setPortfolio(e.target.value)} placeholder="https://" spellCheck={false} autoComplete="url" {...(config.requiresPortfolio ? { "aria-required": "true" as const } : {})} className="w-full border border-black/[0.12] rounded-lg bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111] font-[inherit]" />
					</div>
				)}


			{/* Resume Upload */}
			{!config.hideResume && (
			<div className="flex flex-col gap-2">
				<span className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">Resume</span>
				<label
					htmlFor="field-resume"
					className={`w-full border border-black/[0.12] rounded-lg bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] font-[inherit] flex items-center gap-2 cursor-pointer relative overflow-hidden ${resume ? "text-[#111]" : "text-black/[0.24]"}`}
				>
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="shrink-0">
						<path d="M9.99988 16.0416V10.4166M12.0832 11.8749L9.99988 9.79155L7.9165 11.8749M12.4999 16.0416H15.1039C17.003 16.0416 18.5416 14.5025 18.5416 12.604C18.5416 10.7056 17.003 9.16655 15.1039 9.16655C15.0677 9.16655 15.0315 9.16712 14.9956 9.16822C14.9984 9.09858 14.9999 9.02855 14.9999 8.9582C14.9999 6.19679 12.7613 3.9582 9.99988 3.9582C7.80948 3.9582 5.94816 5.36662 5.27194 7.32721C3.12077 7.60271 1.45822 9.44048 1.45822 11.6666C1.45822 14.0828 3.41697 16.0416 5.83322 16.0416H7.49988" stroke="rgba(0,0,0,0.24)" strokeWidth="1.25" strokeLinecap="square" />
					</svg>
					<span>{resume ? resume.name : "Upload your resume"}</span>
					<input
						id="field-resume"
						name="resume"
						type="file"
						accept=".pdf,.doc,.docx"
						onChange={(e) => setResume(e.target.files?.[0] || null)}
						className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
					/>
				</label>
				{resume && (
					<div className="flex items-center gap-2">
						<span className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">{(resume.size / 1024).toFixed(0)} KB</span>
						<button
							type="button"
							onClick={() => { setResume(null); const el = document.getElementById("field-resume") as HTMLInputElement; if (el) el.value = ""; }}
							className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/32 bg-transparent border-none cursor-pointer p-0"
						>
							Remove
						</button>
					</div>
				)}
			</div>
			)}

			{/* 2. Position-Specific Questions */}
			{config.customQuestions.length > 0 && (
				<div className="flex flex-col gap-6">
					{config.customQuestions.map((question) => (
						<div key={question.id} className="flex flex-col gap-2">
							<label htmlFor={`field-${question.id}`} className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">
								{question.question}
								{question.required && requiredStar}
							</label>
							{question.type === "yesno" ? (
								<div className="flex border border-black/[0.12] rounded-lg overflow-hidden">
									{["Yes", "No"].map((opt) => (
										<button
											key={opt}
											type="button"
											aria-pressed={customAnswers[question.id] === opt}
											onClick={() => setCustomAnswers((prev) => ({ ...prev, [question.id]: opt }))}
											className={`flex-1 px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] border-0 cursor-pointer font-[inherit] transition-[background,color] duration-150 ease-in-out ${customAnswers[question.id] === opt ? "text-[#111] bg-black/[0.04]" : "text-black/32 bg-white"} ${opt === "Yes" ? "border-r border-r-black/[0.12]" : ""}`}
										>
											{opt}
										</button>
									))}
								</div>
							) : question.type === "select" && question.options ? (
								<div className="relative">
									<select
										id={`field-${question.id}`}
										name={question.id}
										value={customAnswers[question.id] || ""}
										onChange={(e) => setCustomAnswers((prev) => ({ ...prev, [question.id]: e.target.value }))}
										{...(question.required ? { "aria-required": "true" as const } : {})}
										className={`w-full border border-black/[0.12] rounded-lg bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] font-[inherit] cursor-pointer appearance-none pr-9 ${customAnswers[question.id] ? "text-[#111]" : "text-black/[0.24]"}`}
									>
										<option value="">Select{"\u2026"}</option>
										{question.options.map((option) => (
											<option key={option} value={option}>{option}</option>
										))}
									</select>
									<svg width="12" height="12" viewBox="0 0 28 28" fill="none" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true">
										<path d="M23.3332 10.5L13.9999 19.8333L4.6665 10.5" stroke="rgba(0,0,0,0.24)" strokeWidth="2.5" strokeLinecap="square" />
									</svg>
								</div>
							) : question.type === "text" ? (
								<input
									id={`field-${question.id}`}
									name={question.id}
									type="text"
									value={customAnswers[question.id] || ""}
									onChange={(e) => setCustomAnswers((prev) => ({ ...prev, [question.id]: e.target.value }))}
									{...(question.required ? { "aria-required": "true" as const } : {})}
									className="w-full border border-black/[0.12] rounded-lg bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111] font-[inherit]"
								/>
							) : (
								<textarea
									id={`field-${question.id}`}
									name={question.id}
									value={customAnswers[question.id] || ""}
									onChange={(e) => setCustomAnswers((prev) => ({ ...prev, [question.id]: e.target.value }))}
									placeholder={"Type here\u2026"}
									rows={4}
									{...(question.required ? { "aria-required": "true" as const } : {})}
									className="w-full border border-black/[0.12] rounded-[10px] bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111] font-[inherit] resize-none"
								/>
							)}
						</div>
					))}
				</div>
			)}

			{/* 3. Social Links */}
			<div>
				<p className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64 mb-6">
					Social media profiles (optional)
				</p>
				<div className="form-grid">
					<div className="flex flex-col gap-2">
						<label htmlFor="field-linkedin" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">LinkedIn</label>
						<div className="prefix-input w-full border border-black/[0.12] rounded-lg bg-white flex p-0 overflow-hidden">
							<span className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/32 bg-black/[0.03] px-3 py-2.5 border-r border-r-black/[0.12] whitespace-nowrap">linkedin.com/in/</span>
							<input id="field-linkedin" name="linkedIn" type="text" value={linkedIn} onChange={(e) => setLinkedIn(stripPrefix(e.target.value, ["https://www.linkedin.com/in/", "https://linkedin.com/in/", "http://linkedin.com/in/", "www.linkedin.com/in/", "linkedin.com/in/"]))} placeholder="handle" spellCheck={false} autoComplete="off" className="w-full border-none rounded-none bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111] font-[inherit] flex-1" />
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<label htmlFor="field-twitter" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">X (Twitter)</label>
						<div className="prefix-input w-full border border-black/[0.12] rounded-lg bg-white flex p-0 overflow-hidden">
							<span className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/32 bg-black/[0.03] px-3 py-2.5 border-r border-r-black/[0.12] whitespace-nowrap">x.com/</span>
							<input id="field-twitter" name="twitter" type="text" value={twitter} onChange={(e) => setTwitter(stripPrefix(e.target.value, ["https://x.com/", "http://x.com/", "x.com/", "https://twitter.com/", "http://twitter.com/", "twitter.com/"]))} placeholder="handle" spellCheck={false} autoComplete="off" className="w-full border-none rounded-none bg-white px-3 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111] font-[inherit] flex-1" />
						</div>
					</div>
				</div>
			</div>

			{/* 4. Privacy + Submit */}
			<div className="flex flex-col gap-6">
				<p className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">
					By submitting my application, I acknowledge that I have read and understand Outpace Studios&#39;{" "}
					<Link href="/careers/privacy" target="_blank" className="text-[#2563eb] underline underline-offset-2">
						Privacy Policy
					</Link>
				</p>
				<label className="flex items-center gap-2.5 cursor-pointer">
					<input
						type="checkbox"
						name="acknowledged"
						checked={acknowledged}
						onChange={(e) => setAcknowledged(e.target.checked)}
					/>
					<span className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#111]">Acknowledge/Confirm</span>
				</label>

				<AnimatePresence>
					{error && (
						<motion.p
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.1 }}
							className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#dc2626]" role="alert">{error}</motion.p>
					)}
				</AnimatePresence>

				<button
					type="submit"
					disabled={state === "submitting"}
					className={`w-full rounded-lg bg-black/[0.88] px-4 py-2.5 text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-white border-none font-[inherit] transition-opacity duration-150 ease-in-out flex items-center justify-center gap-2 ${state === "submitting" ? "cursor-not-allowed opacity-50" : "cursor-pointer opacity-100"}`}
				>
					{state === "submitting" && (
						<svg width="14" height="14" viewBox="0 0 14 14" className="animate-spin" aria-hidden="true">
							<circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.25" />
							<path d="M12.5 7a5.5 5.5 0 0 0-5.5-5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
						</svg>
					)}
					{state === "submitting" ? "Submitting\u2026" : "Submit application"}
				</button>
			</div>
		</motion.form>
		)}
		</AnimatePresence>
		</MotionConfig>
	);
}

// ─── Job Detail Page ─────────────────────────────────────────

export default function JobDetailPage() {
	const params = useParams();
	const slug = params.slug as string;

	const [job, setJob] = React.useState<Job | null | undefined>(undefined);
	const [fetchError, setFetchError] = React.useState(false);
	const containerRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		document.documentElement.dataset.lenisDisabled = "true";
		return () => { delete document.documentElement.dataset.lenisDisabled; };
	}, []);

	React.useEffect(() => {
		fetch(`${CAREERS_CONVEX_URL}/api/query`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ path: "jobs:getBySlug", args: { slug }, format: "json" }),
		})
			.then((res) => {
				if (!res.ok) throw new Error("Failed to fetch job");
				return res.json();
			})
			.then((data) => setJob(data.value))
			.catch(() => {
				setFetchError(true);
				setJob(null);
			});
	}, [slug]);

	React.useEffect(() => {
		if (job === undefined || !containerRef.current) return;
		containerRef.current.style.opacity = "1";
	}, [job]);

	if (job === undefined) {
		return (
			<div className="careers-page min-h-screen bg-black/[0.02] text-[#111]">
				<div className="max-w-[640px] mx-auto px-6 py-16">
					<div className="flex items-center justify-between mb-20">
						<div className="h-3 w-8 rounded-[6px] bg-black/[0.04] animate-pulse" />
						<div className="h-4 w-28 rounded-[6px] bg-black/[0.04] animate-pulse" />
					</div>
					<div className="h-9 w-64 rounded-[6px] bg-black/[0.04] animate-pulse mb-3" />
					<div className="h-4 w-40 rounded-[6px] bg-black/[0.04] animate-pulse mb-10" />
					{[1, 2, 3, 4].map((i) => (
						<div key={i} className="h-4 w-full rounded-[6px] bg-black/[0.04] animate-pulse mb-3" />
					))}
				</div>
			</div>
		);
	}

	if (fetchError) {
		return (
			<div className="careers-page min-h-screen bg-black/[0.02] text-[#111]">
				<div className="max-w-[640px] mx-auto px-6 py-16 text-center">
					<h1 className="text-[14px] font-[500] leading-[20px] tracking-[0.12px]">Something went wrong</h1>
					<p className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] mt-3 text-black/64">Unable to load this position right now. Please try again later.</p>
					<Link href="/careers" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] inline-block mt-6 text-[#2563eb]">
						View all positions
					</Link>
				</div>
			</div>
		);
	}

	if (job === null) {
		return (
			<div className="careers-page min-h-screen bg-black/[0.02] text-[#111]">
				<div className="max-w-[640px] mx-auto px-6 py-16 text-center">
					<h1 className="text-[14px] font-[500] leading-[20px] tracking-[0.12px]">Job not found</h1>
					<p className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] mt-3 text-black/64">This position may have been filled or removed.</p>
					<Link href="/careers" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] inline-block mt-6 text-[#2563eb]">
						View all positions
					</Link>
				</div>
			</div>
		);
	}

	const paragraphs = job.description.split("\n\n");
	const coreReqs = job.requirements.filter((r) => !r.startsWith("Bonus:"));
	const bonusReqs = job.requirements.filter((r) => r.startsWith("Bonus:"));
	const isOpen = job.status === "open";

	return (
		<div ref={containerRef} className="careers-page min-h-screen bg-black/[0.02] text-[#111] opacity-0 transition-opacity duration-[250ms] ease-out">
			<div className="max-w-[640px] mx-auto px-6 py-16">

				<CareersHeader />

				{/* Title + Meta */}
				<div className="mb-6">
					<h1 className="text-[30px] font-[600] leading-[36px] tracking-[-0.3px] text-black/[0.88] text-balance">
						{job.title}{job.status === "closed" && <span className="text-black/32"> (Closed)</span>}
					</h1>
					<div className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/32 mt-3 flex gap-1.5">
						<span>{job.department}</span>
						<span>/</span>
						<span>{job.location}</span>
						<span>/</span>
						<span>{job.type}</span>
					</div>
				</div>

				{/* About the Role */}
				<div className="mb-10">
					{paragraphs.map((p, i) => (
						<p key={i} className={`text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64 text-pretty ${i > 0 ? "mt-4" : ""}`}>{p}</p>
					))}
				</div>

				{/* Key Responsibilities */}
				{job.responsibilities.length > 0 && (
					<section className="mb-10">
						<h2 className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] mb-4">Key Responsibilities</h2>
						<ul role="list" className="flex flex-col gap-3">
							{job.responsibilities.map((item, i) => (
								<li key={i} className="flex items-start gap-3 text-[14px] font-[500] leading-[20px] tracking-[0.12px]">
									<span className="text-black/32">{"\u2192"}</span>
									<span className="text-black/64">{item}</span>
								</li>
							))}
						</ul>
					</section>
				)}

				{/* What we are looking for */}
				{coreReqs.length > 0 && (
					<section className="mb-10">
						<h2 className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] mb-4">Required Qualifications</h2>
						<ul role="list" className="flex flex-col gap-3">
							{coreReqs.map((item, i) => (
								<li key={i} className="flex items-start gap-3 text-[14px] font-[500] leading-[20px] tracking-[0.12px]">
									<span className="text-black/32">{"\u2192"}</span>
									<span className="text-black/64">{item}</span>
								</li>
							))}
						</ul>
					</section>
				)}

				{/* Bonus */}
				{bonusReqs.length > 0 && (
					<section className="mb-10">
						<h2 className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] mb-4">Preferred Experience</h2>
						<ul role="list" className="flex flex-col gap-3">
							{bonusReqs.map((item, i) => (
								<li key={i} className="flex items-start gap-3 text-[14px] font-[500] leading-[20px] tracking-[0.12px]">
									<span className="text-black/32">{"\u2192"}</span>
									<span className="text-black/64">{item.replace("Bonus: ", "")}</span>
								</li>
							))}
						</ul>
					</section>
				)}

				{/* Compensation */}
				{job.salary && (
					<section className="mb-10">
						<h2 className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] mb-4">Compensation</h2>
						<p className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">{job.salary}</p>
					</section>
				)}

				{/* Application Form */}
				{isOpen && (
					<>
						<div className="h-px bg-black/[0.12] my-10" />
						<ApplicationForm job={job} />
					</>
				)}

				{job.status === "closed" && (
					<>
						<div className="h-px bg-black/[0.12] my-10" />
						<div className="flex flex-col items-center gap-3 py-10 text-center">
							<p className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-black/64">
								This position is no longer accepting applications.
							</p>
							<Link href="/careers" className="text-[14px] font-[500] leading-[20px] tracking-[0.12px] text-[#2563eb]">
								View open positions
							</Link>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
